import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { BsFuelPump } from 'react-icons/bs';
import { FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiFileText, FiInfo, FiMapPin, FiNavigation, FiPackage, FiPlay, FiTruck } from 'react-icons/fi';
import { IoSpeedometerOutline } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import Input from '../../components/input';
import LogsComments from '../../components/LogsComments';
import FuelRequestModal from '../../components/modals/FuelRequestModal';
import UnverifiedTripModal from '../../components/modals/UnverifiedTripModal';
import UserAvatar from '../../components/UserAvatar';
import { apiService } from '../../services/apiService';
import { fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import { fetchTripTimeline, getTripDetails, setSelectedTripId, tripStart } from '../../store/Slices/TripSlice';
import '../../styles/TripReadyDispatch.css';
import { TripPermission } from '../../utils/app-constant';
import { canViewTripRouteJourney, checkTripStagePermission, cleanProductName, hasTripPermission, isSuperAdminRole } from '../../utils/helper';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { errorNotify, successNotify } from '../../utils/toast';

const getPayloadData = (response) => response?.data?.data || response?.data || response || [];

const getByPath = (source = {}, path = "") =>
    String(path || "")
        .split(".")
        .reduce((current, key) => (current && current[key] !== undefined ? current[key] : undefined), source);

const getFirstValue = (source = {}, paths = [], fallback = "") => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (value !== undefined && value !== null && value !== "") return value;
    }

    return fallback;
};

const getTripCustomerId = (detail = {}) => {
    const customer = detail?.customer;

    return detail?.customer_id ||
        detail?.customerId ||
        detail?.["3pl_customer_id"] ||
        detail?.customer_info?.id ||
        detail?.customer?.id ||
        detail?.customer?.value ||
        (typeof customer === "number" || (typeof customer === "string" && !Number.isNaN(Number(customer))) ? customer : null);
};

const normalizeNumber = (value) => {
    const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value, fallback = "--") => {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value);
};

const formatNumberValue = (value, fallback = "--") => {
    const parsed = normalizeNumber(value);
    if (parsed === null) return fallback;
    return parsed.toLocaleString();
};

const formatQuantity = (value, fallback = "--") => {
    if (value === undefined || value === null || value === "") return fallback;
    const num = Number(String(value).replace(/,/g, "").trim());
    if (!Number.isFinite(num)) return fallback;
    return Number.isInteger(num) ? String(num) : String(parseFloat(num.toFixed(2)));
};

const formatDistanceValue = (value) => {
    if (value === undefined || value === null || value === "") return "--";
    const parsed = normalizeNumber(value);
    if (parsed === null) return String(value);
    return `${parsed.toLocaleString()} km`;
};

const formatDate = (value) => {
    if (!value) return "";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return parsed.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const hasOngoingTrip = (vehicle) => {
    if (!vehicle || typeof vehicle !== "object") return false;
    const val = vehicle.on_going_trip;
    if (val === null || val === undefined || val === "" || val === false || val === 0) {
        return false;
    }
    return true;
};

const getChecklistRows = (detail = {}, paths = []) => {
    for (const path of paths) {
        const value = getByPath(detail, path);
        if (Array.isArray(value)) return value;
    }

    return [];
};

const getChecklistSummary = (rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const total = safeRows.length;
    const passed = safeRows.filter((row) => {
        const s = normalizeStatus(row?.status);
        return s === "pass" || s === "na" || s === "n/a";
    }).length;
    const failed = safeRows.filter((row) => normalizeStatus(row?.status) === "fail").length;
    const pending = total ? Math.max(total - passed, 0) : 0;

    return { total, passed, failed, pending };
};

const getStopLocationId = (stop = {}) =>
    String(
        getFirstValue(stop, [
            "location_id",
            "customer_warehouse_id",
            "warehouse_id",
            "location.id",
            "location.value",
            "location.customer_warehouse_id",
            "location.warehouse_id",
            "location.warehouse.id",
            "location.warehouse.value",
            "customer_warehouse.id",
            "customer_warehouse.value",
            "customer_warehouse.customer_warehouse_id",
            "customer_warehouse.warehouse_id",
            "customer_warehouse.warehouse.id",
            "customer_warehouse.warehouse.value",
            "warehouse.id",
            "warehouse.value",
        ])
    ).trim();

const getCustomerLocationName = (stop = {}, customerLocations = []) => {
    const stopLocationId = getStopLocationId(stop);
    if (!stopLocationId) return "";

    const matchedLocation = (Array.isArray(customerLocations) ? customerLocations : []).find((location) => {
        const ids = [
            location?.value,
            location?.id,
            location?.customer_warehouse_id,
            location?.warehouse_id,
            location?.raw?.id,
            location?.raw?.value,
            location?.raw?.customer_warehouse_id,
            location?.raw?.warehouse_id,
            location?.raw?.warehouse?.id,
            location?.raw?.warehouse?.value,
        ].map((value) => String(value ?? "").trim()).filter(Boolean);

        return ids.includes(stopLocationId);
    });

    return matchedLocation?.name || matchedLocation?.label || "";
};

const getStopLocationLabel = (stop = {}, customerLocations = []) => {
    const customerLocationName = getCustomerLocationName(stop, customerLocations);
    if (customerLocationName) return customerLocationName;

    const locationName = getFirstValue(stop, [
        "location.warehouse_name",
        "location.name",
        "location.label",
        "location.title",
        "location.location_name",
        "location.point_name",
        "location.warehouse.warehouse_name",
        "location.warehouse.name",
        "location.warehouse.label",
        "location.warehouse.title",
        "customer_warehouse.warehouse_name",
        "customer_warehouse.name",
        "customer_warehouse.label",
        "customer_warehouse.title",
        "customer_warehouse.location_name",
        "customer_warehouse.warehouse.warehouse_name",
        "customer_warehouse.warehouse.name",
        "customer_warehouse.warehouse.label",
        "customer_warehouse.warehouse.title",
        "warehouse.warehouse_name",
        "warehouse.name",
        "warehouse.label",
        "warehouse.title",
        "warehouse_title",
        "warehouse_name",
        "location_name",
        "point_name",
    ]);

    if (locationName) return String(locationName).trim();

    const location = stop?.location;
    if (typeof location === "string" && location.trim()) return location.trim();

    return formatValue(
        getFirstValue(stop, [
            "dispatch_point",
            "decanting_point",
            "start_location",
            "end_location",
            "location_point",
            "location.location_point",
            "location.location",
            "customer_warehouse.address",
            "customer_warehouse.warehouse.address",
            "warehouse.address",
            "location_name",
            "address",
        ]),
        "--"
    );
};

const getStopActionLabel = (stop = {}) => {
    const stopType = String(stop?.stop_type || stop?.stopType || "").trim();
    if (!stopType) return "";

    return stopType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStopDetails = (stop = {}) => {
    const rawProd = stop?.product?.name || stop?.product_name || stop?.product?.label || "";
    const cleanProd = cleanProductName(rawProd);
    const parts = [
        formatDate(stop?.eta || stop?.expected_at || stop?.expected_arrival_at || stop?.start_at || stop?.created_at),
        stop?.quantity ? `${formatNumberValue(stop.quantity)} qty` : "",
        cleanProd,
    ].filter(Boolean);

    return parts.join(" - ") || "--";
};

const getTripStops = (detail = {}) => {
    const rawStops = detail?.trip_stops || detail?.stops || [];
    const sortedStops = (Array.isArray(rawStops) ? [...rawStops] : [])
        .sort((a, b) => (Number(a?.stop_number) || 0) - (Number(b?.stop_number) || 0));

    if (sortedStops.length > 0) return sortedStops;

    const startLocation = getFirstValue(detail, [
        "dispatch_point",
        "start_location",
        "startLocation",
        "origin",
        "from_location",
        "fromLocation",
        "loading_point",
        "pickup_location",
        "pickupLocation",
    ]);
    const endLocation = getFirstValue(detail, [
        "decanting_point",
        "end_location",
        "endLocation",
        "destination",
        "to_location",
        "toLocation",
        "unloading_point",
        "dropoff_location",
        "dropoffLocation",
    ]);

    return [
        startLocation ? { id: "detail-start-location", stop_number: 1, stop_type: "load", location: startLocation } : null,
        endLocation ? { id: "detail-end-location", stop_number: 2, stop_type: "unload", location: endLocation } : null,
    ].filter(Boolean);
};

const getStatusIcon = (isComplete) => isComplete ? <FiCheckCircle className="icon_green" /> : <FiAlertTriangle className="icon_amber" />;

const TripReadyDispatch = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();

    const realTripId = useMemo(() => {
        if (!id) return null;
        return String(id).replace(/^TR-/i, '');
    }, [id]);

    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { customerDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});
    const detailData = useMemo(() => tripDetailsData?.data || tripDetailsData || {}, [tripDetailsData]);

    const [tripDetailsRequestPending, setTripDetailsRequestPending] = useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = useState(null);
    const [fetchedCustomerName, setFetchedCustomerName] = useState("");
    const [customerLocations, setCustomerLocations] = useState([]);
    const [tripStopsModalOpen, setTripStopsModalOpen] = useState(false);
    const [tripStopsLoading, setTripStopsLoading] = useState(false);
    const [tripStops, setTripStops] = useState([]);
    const [initialTripStops, setInitialTripStops] = useState([]);
    const [updatingQuantities, setUpdatingQuantities] = useState(false);
    const [tripStopsFetchError, setTripStopsFetchError] = useState("");
    const [modalTripData, setModalTripData] = useState(null);
    const [modalVehicleData, setModalVehicleData] = useState(null);
    const [ongoingTripModalOpen, setOngoingTripModalOpen] = useState(false);
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);
    const [builtyLoading, setBuiltyLoading] = useState(false);
    const [operationsApproved, setOperationsApproved] = useState(false);
    const [unverifiedModalOpen, setUnverifiedModalOpen] = useState(false);
    const [vehicleChecklistOptions, setVehicleChecklistOptions] = useState([]);
    const [driverDocChecklistOptions, setDriverDocChecklistOptions] = useState([]);
    const [editOpeningFuel, setEditOpeningFuel] = useState('');
    const [editAllocatedFuel, setEditAllocatedFuel] = useState('');
    const [editAllocatedPettyCash, setEditAllocatedPettyCash] = useState('');
    const [showFuelRequestModal, setShowFuelRequestModal] = useState(false);

    useEffect(() => {
        setEditOpeningFuel(String(getFirstValue(detailData, ["vehicle.current_fuel", "opening_fuel", "openingFuel"]) ?? ''));
        setEditAllocatedFuel(String(getFirstValue(detailData, ["allocated_fuel", "allocatedFuel"]) ?? ''));
        setEditAllocatedPettyCash(String(getFirstValue(detailData, ["allocated_petty_cash", "allocatedPettyCash"]) ?? ''));
    }, [detailData]);

    const canStartTrip = useMemo(() => {
        return isSuperAdminRole() || hasTripPermission(TripPermission.TripStart);
    }, []);
    const canStartUnverified = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission(TripPermission.UnverifiedStartTrip) ||
            hasTripPermission("checklist-approval-trip")
        );
    }, []);
    const canViewBuilty = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.Builty, 'Builty', 'builty', 'bilty', 'Bilty', 'trip-builty']), []);
    const canViewRouteJourney = useMemo(() => canViewTripRouteJourney(), []);

    const handleBackToLoadingDispatch = () => {
        const previousStageCheck = checkTripStagePermission("trip-stops");
        if (previousStageCheck.allowed) {
            navigate(`/trips-list/loading-dispatch/${dispatchTripId}`);
        } else {
            errorNotify("You do not have permission to access Loading & Dispatch");
        }
    };

    useEffect(() => {
        const stageCheck = checkTripStagePermission(TripPermission.ChecklistsAndAllocations);
        if (!stageCheck.allowed) {
            errorNotify("You do not have permission to view Ready to Dispatch stage");
            navigate("/trips-list", { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        let isActive = true;

        if (!realTripId) {
            setTripDetailsRequestPending(false);
            setLoadedTripDetailsId(null);
            return () => {
                isActive = false;
            };
        }

        setTripDetailsRequestPending(true);
        dispatch(getTripDetails(realTripId)).finally(() => {
            if (isActive) {
                setLoadedTripDetailsId(realTripId);
                setTripDetailsRequestPending(false);
            }
        });
        dispatch(fetchTripTimeline(realTripId));
        dispatch(setSelectedTripId(realTripId));
        dispatch(fetchCustomerDropdown());

        return () => {
            isActive = false;
        };
    }, [dispatch, realTripId]);

    useEffect(() => {
        let isActive = true;

        Promise.allSettled([
            apiService.getVehicleInspectionChecklistDropdown(),
            apiService.getDriverDocChecklistDropdown(),
        ]).then(([vRes, dRes]) => {
            if (!isActive) return;
            if (vRes.status === "fulfilled") {
                setVehicleChecklistOptions(getPayloadData(vRes.value) || []);
            }
            if (dRes.status === "fulfilled") {
                setDriverDocChecklistOptions(getPayloadData(dRes.value) || []);
            }
        });

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        let isActive = true;

        const fetchCustomerDetailData = async () => {
            const custId = getTripCustomerId(detailData);
            setFetchedCustomerName("");
            setCustomerLocations([]);

            if (!custId) return;

            try {
                const response = await apiService.getCustomerTripDetail(custId);
                const data = response?.data || response || {};
                const customer = detailData?.customer;
                const cName = data?.name ||
                    data?.customer_name ||
                    data?.company_name ||
                    data?.title ||
                    (typeof customer === "object" ? customer?.name : "") ||
                    (typeof customer === "string" && Number.isNaN(Number(customer)) ? customer : "");

                if (isActive && cName) {
                    setFetchedCustomerName(cName);
                }

                const locations = data?.locations ||
                    data?.warehouses ||
                    data?.customer_locations ||
                    data?.customer_warehouses ||
                    [];
                const formattedLocations = (Array.isArray(locations) ? locations : []).map((locationItem) => {
                    const name = getFirstValue(locationItem, [
                        "warehouse_name",
                        "name",
                        "label",
                        "title",
                        "location_name",
                        "point_name",
                        "warehouse.warehouse_name",
                        "warehouse.name",
                        "warehouse.label",
                    ]);

                    return {
                        value: String(locationItem?.id || locationItem?.value || locationItem?.customer_warehouse_id || locationItem?.warehouse_id || locationItem?.warehouse?.id || ""),
                        id: locationItem?.id,
                        customer_warehouse_id: locationItem?.customer_warehouse_id,
                        warehouse_id: locationItem?.warehouse_id || locationItem?.warehouse?.id,
                        name: name || "",
                        label: name || "",
                        raw: locationItem,
                    };
                });

                if (isActive) {
                    setCustomerLocations(formattedLocations);
                }
            } catch (err) {
                console.error("Error fetching customer trip detail:", err);
            }
        };

        fetchCustomerDetailData();

        return () => {
            isActive = false;
        };
    }, [detailData]);

    const tripIdDisplay = detailData?.trip_id || id || "--";
    const dispatchTripId = realTripId || detailData?.id || id;

    const customerName = useMemo(() => {
        const custObj = detailData?.customer;
        if (typeof custObj === "object" && custObj !== null) {
            const nameStr = custObj.name || custObj.label || custObj.title || custObj.customer_name || custObj.company_name;
            if (nameStr) return nameStr;
        }

        const custId = getTripCustomerId(detailData);
        if (Array.isArray(customerDropdownData) && customerDropdownData.length > 0 && custId) {
            const foundCust = customerDropdownData.find(c => String(c.value || c.id) === String(custId));
            if (foundCust) return foundCust.label || foundCust.name || foundCust.title;
        }

        return fetchedCustomerName ||
            (typeof detailData?.customer_name === "string" ? detailData.customer_name : "") ||
            (typeof custObj === "string" && Number.isNaN(Number(custObj)) ? custObj : "") ||
            "--";
    }, [detailData, customerDropdownData, fetchedCustomerName]);

    const vehicleLabel = useMemo(() => {
        const vehicle = detailData?.vehicle || modalVehicleData;
        const registration = vehicle?.registration_number || vehicle?.reg_no || detailData?.vehicle_registration_number || detailData?.vehicle_id;
        const type = detailData?.vehicle_type?.name || detailData?.vehicle_type_name || vehicle?.vehicle_type?.name || vehicle?.type;

        return [registration || "--", type ? `(${type})` : ""].filter(Boolean).join(" ");
    }, [detailData, modalVehicleData]);

    const activeOngoingTrip = useMemo(() => {
        const modalOngoing = modalVehicleData?.on_going_trip;
        if (modalOngoing !== null && modalOngoing !== undefined && modalOngoing !== "" && modalOngoing !== false && modalOngoing !== 0) {
            return modalOngoing;
        }
        const detailOngoing = detailData?.vehicle?.on_going_trip;
        if (detailOngoing !== null && detailOngoing !== undefined && detailOngoing !== "" && detailOngoing !== false && detailOngoing !== 0) {
            return detailOngoing;
        }
        return null;
    }, [modalVehicleData, detailData]);

    const ongoingTripRef = useMemo(() => {
        if (!activeOngoingTrip) return null;
        if (typeof activeOngoingTrip === "object") {
            return activeOngoingTrip.trip_id || activeOngoingTrip.id || activeOngoingTrip.code || null;
        }
        if (typeof activeOngoingTrip === "string" || typeof activeOngoingTrip === "number") {
            return String(activeOngoingTrip);
        }
        return null;
    }, [activeOngoingTrip]);

    const vehicleDetailSystemId = useMemo(() => {
        return getFirstValue(detailData, [
            "vehicle.systemId",
            "vehicle.system_id",
            "vehicle.vehicle_id",
            "vehicle.registration_number",
            "vehicle.reg_no",
            "vehicle_registration_number",
            "vehicle_id",
        ]);
    }, [detailData]);

    const handleVehicleDetailClick = () => {
        if (!vehicleDetailSystemId) return;
        navigate(`/assets/vehicles/${encodeURIComponent(vehicleDetailSystemId)}`, {
            state: {
                vehicle: {
                    ...detailData?.vehicle,
                    systemId: vehicleDetailSystemId,
                },
            },
        });
    };

    const driverName = detailData?.primary_driver_name || detailData?.primary_driver?.name || detailData?.primary_driver?.username || "--";
    const driverPhone = detailData?.primary_driver_contact ? `+${detailData.primary_driver_contact}` : "--";
    const driverImage = detailData?.primary_driver?.image || detailData?.primary_driver?.avatar || null;

    const routeStops = useMemo(() => getTripStops(detailData), [detailData]);
    const routeStopCount = routeStops.length;
    const vehicleChecklistRows = useMemo(() => getChecklistRows(detailData, [
        "vehicle_inspection_checklist",
        "vehicle_inspection_checklists",
        "vehicleInspectionChecklist",
        "trip_vehicle_inspection_checklist",
    ]), [detailData]);
    const driverDocRows = useMemo(() => getChecklistRows(detailData, [
        "driver_doc_checklist",
        "driver_doc_checklists",
        "driverDocumentChecklist",
        "trip_driver_doc_checklist",
    ]), [detailData]);

    const vehicleSummary = useMemo(() => getChecklistSummary(vehicleChecklistRows), [vehicleChecklistRows]);
    const driverDocSummary = useMemo(() => getChecklistSummary(driverDocRows), [driverDocRows]);

    const checklistIssues = useMemo(() => {
        const vehicleIssues = [];
        const driverDocIssues = [];

        // Map of saved vehicle inspection rows
        const savedVehicleMap = new Map();
        (Array.isArray(vehicleChecklistRows) ? vehicleChecklistRows : []).forEach((row) => {
            const id = row?.vehicle_inspection_checklist_id || row?.checklist_item?.id || row?.id;
            if (id !== undefined && id !== null) savedVehicleMap.set(String(id), row);
        });

        // 1. Check each master vehicle option
        (Array.isArray(vehicleChecklistOptions) ? vehicleChecklistOptions : []).forEach((opt) => {
            const optId = String(opt?.value ?? opt?.id ?? "");
            if (!optId) return;
            const saved = savedVehicleMap.get(optId);
            const status = String(saved?.status || '').trim().toLowerCase();
            if (!saved || !status) {
                vehicleIssues.push({
                    id: optId,
                    title: opt?.label || opt?.title || 'Inspection Item',
                    type: 'missing',
                    statusText: 'Not Checked',
                });
            } else if (status === 'fail') {
                vehicleIssues.push({
                    id: optId,
                    title: opt?.label || opt?.title || saved?.checklist_item?.title || 'Inspection Item',
                    type: 'failed',
                    statusText: 'Failed',
                    notes: saved?.notes || saved?.note || '',
                });
            }
        });

        // Also check any saved vehicle row that failed but wasn't in master options
        (Array.isArray(vehicleChecklistRows) ? vehicleChecklistRows : []).forEach((row) => {
            const id = String(row?.vehicle_inspection_checklist_id || row?.checklist_item?.id || row?.id || "");
            const status = String(row?.status || '').trim().toLowerCase();
            if (status === 'fail' && !vehicleIssues.some((i) => i.id === id)) {
                vehicleIssues.push({
                    id,
                    title: row?.checklist_item?.title || row?.title || row?.label || 'Inspection Item',
                    type: 'failed',
                    statusText: 'Failed',
                    notes: row?.notes || row?.note || '',
                });
            }
        });

        // Map of saved driver doc rows
        const savedDriverMap = new Map();
        (Array.isArray(driverDocRows) ? driverDocRows : []).forEach((row) => {
            const id = row?.driver_doc_checklist_id || row?.checklist_item?.id || row?.id;
            if (id !== undefined && id !== null) savedDriverMap.set(String(id), row);
        });

        // 2. Check each master driver option
        (Array.isArray(driverDocChecklistOptions) ? driverDocChecklistOptions : []).forEach((opt) => {
            const optId = String(opt?.value ?? opt?.id ?? "");
            if (!optId) return;
            const saved = savedDriverMap.get(optId);
            const status = String(saved?.status || '').trim().toLowerCase();
            if (!saved || !status) {
                driverDocIssues.push({
                    id: optId,
                    title: opt?.label || opt?.title || 'Driver Document',
                    type: 'missing',
                    statusText: 'Not Reviewed',
                });
            } else if (status === 'fail') {
                driverDocIssues.push({
                    id: optId,
                    title: opt?.label || opt?.title || saved?.checklist_item?.title || 'Driver Document',
                    type: 'failed',
                    statusText: 'Failed',
                    notes: saved?.notes || saved?.note || '',
                });
            }
        });

        // Also check any other saved driver doc row that failed
        (Array.isArray(driverDocRows) ? driverDocRows : []).forEach((row) => {
            const id = String(row?.driver_doc_checklist_id || row?.checklist_item?.id || row?.id || "");
            const status = String(row?.status || '').trim().toLowerCase();
            if (status === 'fail' && !driverDocIssues.some((i) => i.id === id)) {
                driverDocIssues.push({
                    id,
                    title: row?.checklist_item?.title || row?.title || row?.label || 'Driver Document',
                    type: 'failed',
                    statusText: 'Failed',
                    notes: row?.notes || row?.note || '',
                });
            }
        });

        // Fallback: If options haven't loaded yet or are empty, check rows directly
        if (vehicleIssues.length === 0) {
            (Array.isArray(vehicleChecklistRows) ? vehicleChecklistRows : []).forEach((row) => {
                const status = String(row?.status || '').trim().toLowerCase();
                if (status === 'fail') {
                    vehicleIssues.push({
                        id: String(row?.id || Math.random()),
                        title: row?.checklist_item?.title || row?.title || 'Inspection Item',
                        type: 'failed',
                        statusText: 'Failed',
                        notes: row?.notes || row?.note || '',
                    });
                }
            });
        }
        if (driverDocIssues.length === 0) {
            (Array.isArray(driverDocRows) ? driverDocRows : []).forEach((row) => {
                const status = String(row?.status || '').trim().toLowerCase();
                if (status === 'fail') {
                    driverDocIssues.push({
                        id: String(row?.id || Math.random()),
                        title: row?.checklist_item?.title || row?.title || 'Driver Document',
                        type: 'failed',
                        statusText: 'Failed',
                        notes: row?.notes || row?.note || '',
                    });
                }
            });
        }

        const hasIssues = vehicleIssues.length > 0 || driverDocIssues.length > 0;
        return {
            hasIssues,
            vehicleIssues,
            driverDocIssues,
            totalIssuesCount: vehicleIssues.length + driverDocIssues.length,
        };
    }, [vehicleChecklistOptions, driverDocChecklistOptions, vehicleChecklistRows, driverDocRows]);

    const openingFuel = getFirstValue(detailData, ["vehicle.current_fuel", "opening_fuel", "openingFuel"]);
    const startOdometer = useMemo(() => {
        const directOdm = getFirstValue(detailData, ["start_odm"]);
        if (normalizeNumber(directOdm) !== null && normalizeNumber(directOdm) > 0) {
            return directOdm;
        }
        const vehicleOdm = getFirstValue(detailData, [
            "vehicle.odm"
        ]);
        if (normalizeNumber(vehicleOdm) !== null && normalizeNumber(vehicleOdm) > 0) {
            return vehicleOdm;
        }
        return directOdm || vehicleOdm || "";
    }, [detailData]);

    const modalStartOdm = useMemo(() => {
        const vehicleOdm = getFirstValue(modalVehicleData, [
            "odm",
            "odo_meter",
            "odometer",
            "usage_meter_reading",
            "usageMeterReading",
            "meter_reading",
            "current_info.odm",
        ]);
        const tripOdm = getFirstValue(modalTripData, [
            "start_odm",
            "start_odometer",
            "startOdometer",
        ]);
        const detailTripOdm = getFirstValue(detailData, [
            "start_odm",
            "start_odometer",
            "startOdometer",
        ]);
        const detailVehicleOdm = getFirstValue(detailData, [
            "vehicle.odm",
            "vehicle.odo_meter",
            "vehicle.odometer",
            "vehicle.usage_meter_reading",
            "vehicle.current_info.odm",
        ]);

        if (normalizeNumber(vehicleOdm) !== null && normalizeNumber(vehicleOdm) > 0) {
            return vehicleOdm;
        }
        if (normalizeNumber(tripOdm) !== null && normalizeNumber(tripOdm) > 0) {
            return tripOdm;
        }
        if (normalizeNumber(detailVehicleOdm) !== null && normalizeNumber(detailVehicleOdm) > 0) {
            return detailVehicleOdm;
        }
        if (normalizeNumber(detailTripOdm) !== null && normalizeNumber(detailTripOdm) > 0) {
            return detailTripOdm;
        }
        if (normalizeNumber(startOdometer) !== null && normalizeNumber(startOdometer) > 0) {
            return startOdometer;
        }

        const fallback = vehicleOdm || tripOdm || detailVehicleOdm || detailTripOdm || startOdometer;
        return fallback !== undefined && fallback !== null && fallback !== "" ? fallback : null;
    }, [modalVehicleData, modalTripData, detailData, startOdometer]);
    const allocatedFuel = getFirstValue(detailData, ["allocated_fuel", "allocatedFuel"]);
    const allocatedPettyCash = getFirstValue(detailData, ["allocated_petty_cash", "allocatedPettyCash"]);
    const notes = getFirstValue(detailData, ["general_notes", "loading_dispatch_notes", "dispatch_notes", "notes", "note"]);
    const distanceValue = getFirstValue(detailData, ["total_distance", "distance", "trip_distance", "route_distance", "distance_km"]);

    const readinessItems = useMemo(() => ([
        {
            label: "Vehicle Inspection",
            value: vehicleSummary.total ? `${vehicleSummary.passed} / ${vehicleSummary.total} checked` : "--",
            complete: vehicleSummary.total > 0 && vehicleSummary.passed === vehicleSummary.total,
        },
        {
            label: "Driver Documents",
            value: driverDocSummary.total ? `${driverDocSummary.passed} / ${driverDocSummary.total} reviewed` : "--",
            complete: driverDocSummary.total > 0 && driverDocSummary.passed === driverDocSummary.total,
        },
        {
            label: "Start Odometer Reading",
            value: `${formatNumberValue(startOdometer)} km`,
            complete: normalizeNumber(startOdometer) !== null,
        },
        {
            label: "Opening Fuel",
            value: `${formatNumberValue(openingFuel)} Liters`,
            complete: normalizeNumber(openingFuel) !== null,
        },
        {
            label: "Fuel Allocation",
            value: `${formatNumberValue(allocatedFuel)} L approved`,
            complete: true,
        },
        {
            label: "Petty Cash Allocation",
            value: `${formatNumberValue(allocatedPettyCash)} PKR`,
            complete: normalizeNumber(allocatedPettyCash) !== null && normalizeNumber(allocatedPettyCash) > 0,
        },
    ]), [allocatedFuel, allocatedPettyCash, driverDocSummary, openingFuel, startOdometer, vehicleSummary]);

    const completedReadinessCount = readinessItems.filter((item) => item.complete).length;
    const readinessPercent = readinessItems.length ? Math.round((completedReadinessCount / readinessItems.length) * 100) : 0;
    const pendingReadinessCount = Math.max(readinessItems.length - completedReadinessCount, 0);
    const readinessTitle = pendingReadinessCount === 0 ? "Ready" : "Almost Ready";
    const reviewPendingCount = vehicleSummary.pending + driverDocSummary.pending || pendingReadinessCount;

    const isPageLoading = Boolean(
        realTripId && (tripDetailsLoading || tripDetailsRequestPending || loadedTripDetailsId !== realTripId)
    );

    const fetchTripStops = async () => {
        const tripNumericId = Number(dispatchTripId);
        if (!tripNumericId) return;

        setTripStopsLoading(true);
        setTripStopsFetchError("");
        try {
            const response = await apiService.getTripStopsByTripId(tripNumericId);
            const rootData = response?.data !== undefined ? response.data : response;

            let rawStops = [];
            let tripInfo = null;
            let vehicleInfo = null;

            if (Array.isArray(rootData)) {
                rawStops = rootData;
            } else if (rootData && typeof rootData === "object") {
                if (Array.isArray(rootData.stop)) {
                    rawStops = rootData.stop;
                } else if (Array.isArray(rootData.stops)) {
                    rawStops = rootData.stops;
                } else if (Array.isArray(rootData.trip_stops)) {
                    rawStops = rootData.trip_stops;
                }
                tripInfo = rootData.trip || null;
                vehicleInfo = rootData.vehicle || null;
            }

            setModalTripData(tripInfo);
            setModalVehicleData(vehicleInfo);

            if (vehicleInfo && hasOngoingTrip(vehicleInfo)) {
                setTripStopsModalOpen(false);
                setOngoingTripModalOpen(true);
                return;
            }

            const stopsList = (Array.isArray(rawStops) ? rawStops : []).map((stop, idx) => ({
                id: stop.id,
                trip_id: stop.trip_id,
                stop_number: stop.stop_number || idx + 1,
                stop_type: stop.stop_type || "load",
                load_type: stop.load_type,
                location: stop.location || "--",
                location_id: stop.location_id,
                product_id: stop.product_id,
                product_name: cleanProductName(stop.product?.name || stop.product_name) || "--",
                product_load_value: stop.product_load_value,
                product_unload_value: stop.product_unload_value,
                quantity: stop.quantity !== null && stop.quantity !== undefined ? formatQuantity(stop.quantity) : "",
            }));
            setTripStops(stopsList);
            setInitialTripStops(JSON.parse(JSON.stringify(stopsList)));
        } catch (error) {
            const msg = typeof error === "string" ? error : error?.message || "Failed to fetch trip stops";
            setTripStopsFetchError(msg);
            errorNotify(msg);
        } finally {
            setTripStopsLoading(false);
        }
    };

    const handleOpenStartTripModal = async () => {
        if (!canStartTrip) {
            errorNotify("You do not have permission to move this trip to In Transit");
            return;
        }

        if (hasOngoingTrip(detailData?.vehicle) || hasOngoingTrip(modalVehicleData)) {
            setOngoingTripModalOpen(true);
            return;
        }

        if (checklistIssues.hasIssues && !canStartUnverified) {
            setUnverifiedModalOpen(true);
            return;
        }

        setTripStopsModalOpen(true);
        await fetchTripStops();
    };

    const handleProceedSeniorOverride = async () => {
        setUnverifiedModalOpen(false);
        if (hasOngoingTrip(detailData?.vehicle) || hasOngoingTrip(modalVehicleData)) {
            setOngoingTripModalOpen(true);
            return;
        }
        setOperationsApproved(true);
        setTripStopsModalOpen(true);
        await fetchTripStops();
    };

    const handleStopValueChange = (stopId, field, value) => {
        setTripStops((prevStops) =>
            prevStops.map((stop) =>
                stop.id === stopId ? { ...stop, [field]: value } : stop
            )
        );
    };

    const quantityValidationError = useMemo(() => {
        if (!tripStops || tripStops.length === 0) return null;

        let totalLoad = 0;
        let totalUnload = 0;
        let hasLoad = false;
        let hasUnload = false;

        const productTotals = {};

        for (const stop of tripStops) {
            const rawType = String(stop.stop_type || "").trim().toLowerCase();
            const qty = Number(stop.quantity);

            if (Number.isFinite(qty) && qty < 0) {
                return "Quantity cannot be negative.";
            }

            const validQty = Number.isFinite(qty) ? qty : 0;
            const cleanProdName = cleanProductName(stop.product_name || stop.product?.name || "");
            const prodKey = stop.product_id ? String(stop.product_id) : (cleanProdName || "default");

            if (!productTotals[prodKey]) {
                productTotals[prodKey] = {
                    productName: cleanProdName || "Product",
                    load: 0,
                    unload: 0,
                };
            }

            if (rawType === "load") {
                totalLoad += validQty;
                hasLoad = true;
                productTotals[prodKey].load += validQty;
            } else if (rawType === "unload") {
                totalUnload += validQty;
                hasUnload = true;
                productTotals[prodKey].unload += validQty;
            }
        }

        for (const key of Object.keys(productTotals)) {
            const prod = productTotals[key];
            if (prod.unload > prod.load) {
                const prodLabel = prod.productName && prod.productName !== "Product" ? ` for ${prod.productName}` : "";
                return `Unload quantity (${prod.unload}) cannot be greater than load quantity (${prod.load})${prodLabel}.`;
            }
        }

        if (hasLoad && hasUnload && totalUnload > totalLoad) {
            return `Unload quantity (${totalUnload}) cannot be greater than load quantity (${totalLoad}).`;
        }

        return null;
    }, [tripStops]);

    const hasQuantitiesChanged = useMemo(() => {
        if (!tripStops.length || !initialTripStops.length) return false;
        return tripStops.some((stop) => {
            const initial = initialTripStops.find((s) => s.id === stop.id);
            if (!initial) return false;
            return String(stop.quantity).trim() !== String(initial.quantity).trim();
        });
    }, [tripStops, initialTripStops]);

    const modalSummaryTotals = useMemo(() => {
        let totalLoad = 0;
        let totalUnload = 0;
        let totalReturn = 0;

        for (const stop of tripStops) {
            const rawType = String(stop.stop_type || "").trim().toLowerCase();
            const qty = Number(stop.quantity) || 0;
            if (rawType === "load") totalLoad += qty;
            else if (rawType === "unload") totalUnload += qty;
            else if (rawType === "return") totalReturn += qty;
        }

        return {
            totalLoad,
            totalUnload,
            totalReturn,
            isBalanced: totalLoad > 0 && totalLoad === totalUnload,
        };
    }, [tripStops]);

    const handleUpdateQuantities = async () => {
        if (quantityValidationError) {
            errorNotify(quantityValidationError);
            return false;
        }

        const tripNumericId = Number(dispatchTripId);
        if (!tripNumericId) return false;

        setUpdatingQuantities(true);
        try {
            const payload = {
                trip_id: tripNumericId,
                trip_stops: tripStops.map((stop) => ({
                    id: Number(stop.id),
                    quantity: stop.quantity === "" || stop.quantity === null
                        ? 0
                        : Number(stop.quantity),
                })),
            };

            await apiService.updateTripStopsQuantity(payload);
            successNotify("Trip stops quantity updated successfully");
            setInitialTripStops(JSON.parse(JSON.stringify(tripStops)));
            return true;
        } catch (error) {
            const msg = typeof error === "string" ? error : error?.message || "Failed to update trip stops quantity";
            errorNotify(msg);
            return false;
        } finally {
            setUpdatingQuantities(false);
        }
    };

    const handleStartTripFromModal = async () => {
        if (!dispatchTripId || statusChangeLoading || updatingQuantities) return;
        if (!canStartTrip) {
            errorNotify("You do not have permission to move this trip to In Transit");
            setTripStopsModalOpen(false);
            return;
        }

        if (hasOngoingTrip(modalVehicleData) || hasOngoingTrip(detailData?.vehicle)) {
            setTripStopsModalOpen(false);
            setOngoingTripModalOpen(true);
            return;
        }

        if (quantityValidationError) {
            errorNotify(quantityValidationError);
            return;
        }

        if (hasQuantitiesChanged) {
            const updated = await handleUpdateQuantities();
            if (!updated) return;
        }

        setStatusChangeLoading(true);
        try {
            const rawStartOdm = modalStartOdm ?? startOdometer ?? 0;
            const startOdm = Number(rawStartOdm) || 0;

            const payload = {
                trip_id: Number(dispatchTripId) || dispatchTripId,
                start_odm: startOdm,
                ...(editOpeningFuel !== '' ? { opening_fuel: Number(editOpeningFuel) || 0 } : {}),
                ...(editAllocatedFuel !== '' ? { allocated_fuel: Number(editAllocatedFuel) || 0 } : {}),
                ...(editAllocatedPettyCash !== '' ? { allocated_petty_cash: Number(editAllocatedPettyCash) || 0 } : {}),
                ...(operationsApproved || canStartUnverified ? { operations_approved: true } : {}),
            };

            await dispatch(tripStart(payload)).unwrap();
            successNotify("Trip started successfully.");
            setTripStopsModalOpen(false);
            setOperationsApproved(false);
            const nextStageCheck = checkTripStagePermission("in_transit");
            if (nextStageCheck.allowed) {
                navigate(`/trips-list/in-transit/${dispatchTripId}`, { replace: true });
            } else {
                navigate("/trips-list", { replace: true });
            }
        } catch (error) {
            errorNotify(error || "Failed to start trip");
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleOpenBuilty = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setBuiltyLoading(true);
        try {
            const response = await apiService.getBilty(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Builty URL not found");
            }
        } catch (error) {
            console.error("Error generating builty:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate builty");
        } finally {
            setBuiltyLoading(false);
        }
    };

    if (isPageLoading) {
        return <SpinnerLoader />;
    }

    return (
        <div className="trip_ready_dispatch_page">
            {/* Desktop Header */}
            <div className="desktop_header_only">
                <Breadcrumbs values={["Trip Management", "Trip Detail", "Ready to Dispatch"]} />
                <div className="access_view ready_top_bar">
                    <h2>Ready to Dispatch</h2>
                    <div className="ready_top_actions">
                        {canViewBuilty && (
                            <button
                                type="button"
                                className="btn_builty"
                                onClick={handleOpenBuilty}
                                disabled={builtyLoading}
                                title="View Builty PDF"
                            >
                                {builtyLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                        <span>Loading...</span>
                                    </>
                                ) : (
                                    <>
                                        <FiFileText style={{ fontSize: '14px' }} />
                                        <span>Builty</span>
                                    </>
                                )}
                            </button>
                        )}
                        <Button
                            cancelBtn={handleBackToLoadingDispatch}
                            cancelBtnText="Back"
                            shouldRow={false}
                            isShowSubmitBtn={false}
                        />
                    </div>
                </div>
            </div>

            {/* Mobile Header */}
            <div className="mobile_header_only mobile_ready_header">
                <div className="mobile_header_left_controls">
                    <button className="mobile_menu_icon_btn" onClick={handleBackToLoadingDispatch} type="button" title="Back">
                        <FiArrowLeft style={{ fontSize: '20px' }} />
                    </button>
                    <h1 className="mobile_header_title">Ready to Dispatch</h1>
                </div>
                {canViewBuilty && (
                    <button
                        type="button"
                        className="btn_builty mobile_ready_builty_btn"
                        onClick={handleOpenBuilty}
                        disabled={builtyLoading}
                        title="View Builty PDF"
                    >
                        {builtyLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                        ) : (
                            <FiFileText style={{ fontSize: '14px' }} />
                        )}
                        <span>Builty</span>
                    </button>
                )}
            </div>

            {/* Main Content Layout */}
            <Row className="ready_main_row g-3">
                <Col lg={8} md={12} className="ready_left_col">
                    <div className="ready_card start_trip_main_card">
                        <div className="ready_card_header">
                            <div>
                                <h3 className="title">Start Trip</h3>
                                <p className="subtitle">Verify the route information and start the trip by tapping Play.</p>
                            </div>
                            <div className="trip_id_purple_pill">
                                Trip ID: {tripIdDisplay}
                            </div>
                        </div>

                        <div className="ready_meta_info_card">
                            <div className="meta_info_item">
                                <span className="meta_info_label">Customer</span>
                                <span className="meta_info_value">{customerName}</span>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Vehicle</span>
                                <span className="meta_info_value">
                                    {vehicleDetailSystemId ? (
                                        <button
                                            type="button"
                                            className="meta_info_value_link meta_info_value_link_button"
                                            onClick={handleVehicleDetailClick}
                                        >
                                            {vehicleLabel}
                                        </button>
                                    ) : (
                                        vehicleLabel
                                    )}
                                </span>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Driver</span>
                                <div className="driver_meta_row">
                                    <UserAvatar username={driverName} image={driverImage} size={28} />
                                    <span className="driver_name_link">{driverName}</span>
                                </div>
                            </div>
                            <div className="meta_info_item">
                                <span className="meta_info_label">Driver Contact</span>
                                <span className="meta_info_value">{driverPhone}</span>
                            </div>
                        </div>

                        <div className="checklist_section_card" style={{ margin: '16px 0' }}>
                            <div className="checklist_section_header">
                                <h4 className="checklist_section_title">Fuel & Petty Cash</h4>
                            </div>
                            <Row className="g-3">
                                <Col md={6} sm={12}>
                                    <Input
                                        label="Opening Fuel"
                                        name="opening_fuel"
                                        type="text"
                                        value={editOpeningFuel}
                                        onChange={(e) => setEditOpeningFuel(e.target.value)}
                                        showExtraText={true}
                                        extraText="ltrs"
                                        required={false}
                                        disabled={true}
                                    />
                                </Col>
                                <Col md={6} sm={12}>
                                    <Input
                                        label="Allocated Fuel"
                                        name="allocated_fuel"
                                        type="text"
                                        value={editAllocatedFuel}
                                        onChange={(e) => setEditAllocatedFuel(e.target.value)}
                                        showExtraText={true}
                                        extraText="ltrs"
                                        required={false}
                                        disabled={true}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            className="fuel_request_action_btn"
                                            onClick={() => setShowFuelRequestModal(true)}
                                        >
                                            + Fuel Request
                                        </button>
                                    </div>
                                </Col>
                                <Col md={6} sm={12}>
                                    <Input
                                        label="Allocated Petty Cash"
                                        name="allocated_petty_cash"
                                        type="number"
                                        min={0}
                                        value={editAllocatedPettyCash}
                                        onChange={(e) => setEditAllocatedPettyCash(e.target.value)}
                                        showExtraText={true}
                                        extraText="PKR"
                                        required={false}
                                    />
                                </Col>
                            </Row>
                        </div>

                        {canStartTrip && (
                            <div className="royal_blue_start_card">
                                <div className="blue_card_left">
                                    <div className="play_button_wrapper">
                                        <div className="ripple_ring ring_outer"></div>
                                        <div className="ripple_ring ring_inner"></div>
                                        <button
                                            className="play_circle_btn"
                                            type="button"
                                            onClick={handleOpenStartTripModal}
                                            title="Start Trip"
                                            disabled={statusChangeLoading}
                                        >
                                            <FiPlay className="play_icon" />
                                        </button>
                                    </div>
                                    <div className="blue_card_text">
                                        <h4 className="blue_title">Start Trip</h4>
                                        <p className="blue_sub">Records status change and moves this trip to In Transit</p>
                                    </div>
                                </div>
                                <div className="blue_card_right">
                                    <div className="blue_stat">
                                        <span className="stat_label">Distance</span>
                                        <span className="stat_value">{formatDistanceValue(distanceValue)}</span>
                                    </div>
                                    <div className="blue_stat_divider"></div>
                                    <div className="blue_stat">
                                        <span className="stat_label">Stops</span>
                                        <span className="stat_value">{routeStops.length ? `${routeStops.length} Points` : "--"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {pendingReadinessCount > 0 ? (
                            <div className="amber_warning_banner">
                                <FiAlertTriangle className="warning_icon" />
                                <span>{reviewPendingCount} readiness item{reviewPendingCount > 1 ? "s" : ""} pending review before departure.</span>
                            </div>
                        ) : (
                            <div className="ready_success_banner">
                                <FiCheckCircle className="success_icon" />
                                <span>All readiness checks are cleared for dispatch.</span>
                            </div>
                        )}

                        {canViewRouteJourney && <div className="route_journey_section">
                            <div className="journey_header">
                                <h3 className="journey_title">Route Journey</h3>
                                <span className="journey_sub_info">
                                    {formatDistanceValue(distanceValue)} · {routeStopCount} {routeStopCount === 1 ? 'stop' : 'stops'}
                                </span>
                            </div>

                            <div className="journey_timeline_container">
                                {routeStops.length > 0 ? (
                                    routeStops.map((stop, index) => {
                                        const isFirst = index === 0;
                                        const isLast = index === routeStops.length - 1;
                                        const badgeLabel = isFirst ? "Trip Start" : isLast ? "Trip End" : `Stop-${index}`;
                                        const actionLabel = getStopActionLabel(stop);

                                        return (
                                            <div className="timeline_stop_item" key={stop?.id || `${index}-${getStopLocationLabel(stop, customerLocations)}`}>
                                                {isFirst || isLast ? (
                                                    <div className="timeline_node_ring node_green_ring"></div>
                                                ) : (
                                                    <div className="timeline_node_circle">{index}</div>
                                                )}
                                                <div className="timeline_stop_content">
                                                    <div className="stop_badge_row">
                                                        <span className={`stop_pill_tag ${isFirst || isLast ? "tag_purple" : "tag_red"}`}>{badgeLabel}</span>
                                                        {actionLabel && <span className="stop_action_tag">{actionLabel}</span>}
                                                    </div>
                                                    <h4 className="stop_location_title">{getStopLocationLabel(stop, customerLocations)}</h4>
                                                    <p className="stop_details_text">{getStopDetails(stop)}</p>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="ready_empty_state">No route stops found.</div>
                                )}
                            </div>
                        </div>}
                    </div>
                </Col>

                <Col lg={4} md={12} className="ready_right_col">
                    <div className="ready_card gauge_summary_card">
                        <div className="gauge_circular_row">
                            <div className="circular_progress_box">
                                <svg viewBox="0 0 36 36" className="circular_chart">
                                    <path className="circle_bg"
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path className="circle"
                                        strokeDasharray={`${readinessPercent}, 100`}
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <text x="18" y="20.35" className="percentage">{readinessPercent}%</text>
                                </svg>
                            </div>
                            <div className="gauge_text_content">
                                <h4 className="readiness_title">{readinessTitle}</h4>
                                <p className="readiness_subtitle">{completedReadinessCount} of {readinessItems.length} checks cleared</p>
                            </div>
                        </div>

                        <div className="checklist_summary_list">
                            {readinessItems.map((item) => (
                                <div className="summary_item_row" key={item.label}>
                                    <div className="summary_left">
                                        {getStatusIcon(item.complete)}
                                        <span className="summary_label">{item.label}</span>
                                    </div>
                                    <span className={item.complete ? "summary_val_muted" : "summary_val_amber"}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="driver_details_sidebar service_cost_summary_details mt-3">
                        <LogsComments />
                    </div>

                    <div className="trip_notes_main_div mt-3">
                        <h5>General Notes</h5>
                        <div style={{ padding: '0 16px 16px 16px' }}>
                            <Input
                                label=""
                                name="general_notes"
                                placeholder="No notes added."
                                quill={true}
                                value={notes}
                                disabled={true}
                                required={false}
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            {canStartTrip && (
                <CustomModal
                    show={tripStopsModalOpen}
                    handleClose={() => !statusChangeLoading && !updatingQuantities && setTripStopsModalOpen(false)}
                    heading="Trip Stops - Dispatch Review"
                    size="xl"
                    dialogClassName="trip_stops_dispatch_dialog"
                >
                    <div className="dispatch_modal_content">
                        {/* Trip & Vehicle Overview Header Card */}
                        <div className="dispatch_modal_header_card">
                            <div className="dispatch_modal_header_top">
                                <div className="dispatch_modal_heading_group">
                                    <span className="dispatch_trip_pill">
                                        <span className="pill_label_trip">Trip ID: </span><strong>{modalTripData?.trip_id || tripIdDisplay}</strong>
                                    </span>
                                    <span className="dispatch_status_pill">
                                        {modalTripData?.status ? String(modalTripData.status).replace(/_/g, ' ').toUpperCase() : 'READY TO DISPATCH'}
                                    </span>
                                </div>
                                <div className="dispatch_modal_stops_count">
                                    <span className="stops_badge">
                                        {tripStops.length} {tripStops.length === 1 ? 'Stop' : 'Stops'}
                                    </span>
                                </div>
                            </div>

                            {/* 4-Card Stats Grid */}
                            <div className="dispatch_metrics_grid">
                                {/* 1. Vehicle */}
                                <div className="dispatch_metric_tile">
                                    <div className="metric_icon_box icon_vehicle">
                                        <FiTruck />
                                    </div>
                                    <div className="metric_info">
                                        <span className="metric_label">Vehicle</span>
                                        <span className="metric_value" title={vehicleLabel}>
                                            {vehicleLabel}
                                        </span>
                                    </div>
                                </div>

                                {/* 2. Trip Start ODM */}
                                <div className="dispatch_metric_tile highlight_metric">
                                    <div className="metric_icon_box icon_start_odm">
                                        <IoSpeedometerOutline />
                                    </div>
                                    <div className="metric_info">
                                        <span className="metric_label">Trip Start ODM</span>
                                        <span className="metric_value odm_value">
                                            {normalizeNumber(modalStartOdm) !== null
                                                ? `${formatNumberValue(modalStartOdm)} km`
                                                : "--"}
                                        </span>
                                    </div>
                                </div>

                                {/* 3. Distance */}
                                <div className="dispatch_metric_tile">
                                    <div className="metric_icon_box icon_distance">
                                        <FiNavigation />
                                    </div>
                                    <div className="metric_info">
                                        <span className="metric_label">Distance</span>
                                        <span className="metric_value" title={formatDistanceValue(modalTripData?.distance ?? distanceValue)}>
                                            {formatDistanceValue(modalTripData?.distance ?? distanceValue)}
                                        </span>
                                    </div>
                                </div>

                                {/* 4. Allocated Fuel */}
                                <div className="dispatch_metric_tile">
                                    <div className="metric_icon_box icon_fuel">
                                        <BsFuelPump />
                                    </div>
                                    <div className="metric_info">
                                        <span className="metric_label">Allocated Fuel</span>
                                        <span className="metric_value" title={normalizeNumber(modalTripData?.allocated_fuel ?? allocatedFuel) !== null ? `${formatNumberValue(modalTripData?.allocated_fuel ?? allocatedFuel)} L` : "--"}>
                                            {normalizeNumber(modalTripData?.allocated_fuel ?? allocatedFuel) !== null
                                                ? `${formatNumberValue(modalTripData?.allocated_fuel ?? allocatedFuel)} L`
                                                : "--"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Instructional / Review Note */}
                        <div className="dispatch_modal_instruction">
                            <FiInfo className="info_icon" />
                            <span>
                                Review stop details and update product quantities before starting the trip. Only product quantity is editable.
                            </span>
                        </div>

                        {tripStopsLoading ? (
                            <div className="dispatch_stops_loading">
                                <span className="spinner-border text-primary spinner-border-sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Loading trip stops...</span>
                            </div>
                        ) : tripStopsFetchError ? (
                            <div className="dispatch_stops_error">
                                <FiAlertTriangle className="error_icon" />
                                <span>{tripStopsFetchError}</span>
                                <button type="button" className="btn btn-sm btn-outline-primary ms-3" onClick={fetchTripStops}>
                                    Retry
                                </button>
                            </div>
                        ) : tripStops.length === 0 ? (
                            <div className="dispatch_stops_empty">
                                <p className="mb-0">No trip stops found for this trip.</p>
                            </div>
                        ) : (
                            <div className="dispatch_stops_table_wrapper">
                                <table className="dispatch_stops_table">
                                    <thead>
                                        <tr>
                                            <th className="col_center" style={{ width: '70px' }}>Stop</th>
                                            <th className="col_center" style={{ width: '110px' }}>Type</th>
                                            <th className="col_left">Location</th>
                                            <th className="col_left" style={{ width: '220px' }}>Product</th>
                                            <th className="col_right" style={{ width: '180px' }}>Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tripStops.map((stop, idx) => {
                                            const rawType = String(stop.stop_type || "").trim().toLowerCase();
                                            const isLoad = rawType === 'load';
                                            const isUnload = rawType === 'unload';
                                            const isReturn = rawType === 'return';
                                            const isInvalidQty = !isLoad && Boolean(quantityValidationError);
                                            return (
                                                <tr key={stop.id || idx}>
                                                    <td className="col_center">
                                                        <span className="stop_number_circle">
                                                            #{stop.stop_number || idx + 1}
                                                        </span>
                                                    </td>
                                                    <td className="col_center">
                                                        <span className={`stop_type_badge ${isLoad ? 'stop_type_load' : isUnload ? 'stop_type_unload' : isReturn ? 'stop_type_return' : 'stop_type_load'}`}>
                                                            {isLoad ? 'Load' : isUnload ? 'Unload' : isReturn ? 'Return' : stop.stop_type}
                                                        </span>
                                                    </td>
                                                    <td className="col_left">
                                                        <div className="stop_location_cell">
                                                            <FiMapPin className="cell_icon location_icon" />
                                                            <span className="location_text" title={stop.location}>
                                                                {stop.location || '--'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="col_left">
                                                        <div className="stop_product_cell">
                                                            <div className="product_icon_wrapper">
                                                                <FiPackage className="cell_icon product_icon" />
                                                            </div>
                                                            <div className="product_text_wrapper">
                                                                <span className="product_name_text" title={stop.product_name}>
                                                                    {cleanProductName(stop.product_name) || '--'}
                                                                </span>
                                                                {stop.load_type && (
                                                                    <span className="product_load_type">
                                                                        {stop.load_type} load
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="col_right">
                                                        <div className="qty_input_group">
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                min="0"
                                                                className={`dispatch_input_value ${isInvalidQty ? 'is_invalid_qty' : ''}`}
                                                                placeholder="0"
                                                                value={stop.quantity}
                                                                onChange={(e) => handleStopValueChange(stop.id, 'quantity', e.target.value)}
                                                                disabled={statusChangeLoading || updatingQuantities}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {quantityValidationError && (
                            <div className="dispatch_validation_banner">
                                <FiAlertTriangle className="error_icon" />
                                <span>{quantityValidationError}</span>
                            </div>
                        )}

                        <div className="dispatch_modal_footer">
                            <div className="dispatch_footer_left">
                                {tripStops.length > 0 && (
                                    <span className="footer_total_stops">
                                        Total {tripStops.length} {tripStops.length === 1 ? 'stop' : 'stops'}
                                    </span>
                                )}
                                {modalSummaryTotals.isBalanced && (
                                    <span className="footer_balanced_badge">
                                        <FiCheckCircle style={{ fontSize: '13px' }} /> Balanced Quantities
                                    </span>
                                )}
                                {hasQuantitiesChanged && !quantityValidationError && (
                                    <span className="dispatch_unsaved_indicator">● Modified (Unsaved)</span>
                                )}
                            </div>
                            <div className="dispatch_footer_actions">
                                <button
                                    type="button"
                                    className="btn_dispatch_cancel"
                                    onClick={() => setTripStopsModalOpen(false)}
                                    disabled={statusChangeLoading || updatingQuantities}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn_dispatch_update"
                                    onClick={handleUpdateQuantities}
                                    disabled={!hasQuantitiesChanged || Boolean(quantityValidationError) || statusChangeLoading || updatingQuantities || tripStopsLoading || tripStops.length === 0}
                                    title={!hasQuantitiesChanged ? "No quantity changes to save" : "Save updated quantities"}
                                >
                                    {updatingQuantities ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        <span>Update Quantities</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn_dispatch_start"
                                    onClick={handleStartTripFromModal}
                                    disabled={Boolean(quantityValidationError) || statusChangeLoading || updatingQuantities || tripStopsLoading}
                                >
                                    {statusChangeLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                            <span>Starting Trip...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiPlay style={{ fontSize: '13px' }} />
                                            <span>Start Trip</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </CustomModal>
            )}

            <UnverifiedTripModal
                show={unverifiedModalOpen}
                onClose={() => setUnverifiedModalOpen(false)}
                tripId={tripIdDisplay}
                vehicleIssues={checklistIssues.vehicleIssues}
                driverDocIssues={checklistIssues.driverDocIssues}
                canStartUnverified={canStartUnverified}
                onProceedOverride={handleProceedSeniorOverride}
                loading={statusChangeLoading}
            />

            <FuelRequestModal
                show={showFuelRequestModal}
                onClose={() => setShowFuelRequestModal(false)}
                tripId={Number(dispatchTripId) || dispatchTripId}
                tripCode={tripIdDisplay}
                vehicleId={detailData?.vehicle?.id || detailData?.vehicle_id}
                vehicleName={vehicleLabel}
                allocatedFuel={detailData?.allocated_fuel}
                onSuccess={() => {
                    if (dispatchTripId) dispatch(getTripDetails(dispatchTripId));
                }}
            />

            <CustomModal
                show={ongoingTripModalOpen}
                handleClose={() => setOngoingTripModalOpen(false)}
                heading="Cannot Start Trip"
                size="md"
                dialogClassName="ongoing_trip_error_dialog"
            >
                <div className="ongoing_trip_modal_content">
                    <div className="ongoing_trip_icon_badge">
                        <FiAlertTriangle />
                    </div>

                    <h3 className="ongoing_trip_modal_title">
                        This vehicle is currently on an active trip
                    </h3>
                    <p className="ongoing_trip_modal_subtitle">
                        This vehicle currently has an active trip in transit. A new trip cannot be started until the ongoing trip is completed.
                    </p>

                    <div className="ongoing_trip_info_box">
                        <div className="ongoing_info_row">
                            <span className="info_label">Vehicle</span>
                            <span className="info_value">{vehicleLabel}</span>
                        </div>
                        <div className="ongoing_info_row">
                            <span className="info_label">Vehicle Status</span>
                            <span className="ongoing_status_pill">In Transit</span>
                        </div>
                        {ongoingTripRef && (
                            <div className="ongoing_info_row">
                                <span className="info_label">Active Trip</span>
                                <span className="info_value fw-bold">#{ongoingTripRef}</span>
                            </div>
                        )}
                    </div>

                    <div className="ongoing_trip_modal_footer">
                        <button
                            type="button"
                            className="btn_ongoing_modal_close"
                            onClick={() => setOngoingTripModalOpen(false)}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default TripReadyDispatch;
